export default {
  namespaced: true,
  state: {
    articles: [],
    currentArticle: null,
    loading: false,
    error: null,
  },
  mutations: {
    SET_ARTICLES(state, articles) {
      state.articles = articles
    },
    SET_CURRENT_ARTICLE(state, article) {
      state.currentArticle = article
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
  },
  actions: {
    async fetchArticles({ commit }, { category, subcategory } = {}) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const baseUrl = process.env.VUE_APP_COS_BASE_URL || ''
        // 先加载 index.json（文章列表索引）
        const url = category
          ? `${baseUrl}/articles/${category}/index.json`
          : `${baseUrl}/articles/index.json`
        const resp = await fetch(url)
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
        let articles = await resp.json()
        if (subcategory) {
          articles = articles.filter(a => a.subcategory === subcategory)
        }
        commit('SET_ARTICLES', articles)
        commit('SET_LOADING', false)
        return articles
      } catch (error) {
        console.error('Error fetching articles:', error)
        commit('SET_ARTICLES', [])
        commit('SET_LOADING', false)
        return []
      }
    },

    async fetchArticleBySlug({ commit }, { category, slug }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const baseUrl = process.env.VUE_APP_COS_BASE_URL || ''
        const url = `${baseUrl}/articles/${category}/${slug}.json`
        const resp = await fetch(url)
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
        const article = await resp.json()
        const decision = article && article.audit && article.audit.decision
        if (decision === 'fail' || decision === 'unavailable') {
          throw new Error(`Article gated by audit: ${decision}`)
        }
        commit('SET_CURRENT_ARTICLE', article)
        commit('SET_LOADING', false)
        return article
      } catch (error) {
        console.error(`Error fetching article ${category}/${slug}:`, error)
        commit('SET_ERROR', 'Article not found')
        commit('SET_CURRENT_ARTICLE', null)
        commit('SET_LOADING', false)
        return null
      }
    },

    async loadAllArticles({ commit }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const baseUrl = process.env.VUE_APP_COS_BASE_URL || ''
        const url = `${baseUrl}/articles/index.json`
        const resp = await fetch(url)
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
        const allArticles = await resp.json()
        commit('SET_ARTICLES', allArticles)
        commit('SET_LOADING', false)
        return allArticles
      } catch (error) {
        console.error('Error loading all articles:', error)
        commit('SET_ERROR', error.message)
        commit('SET_LOADING', false)
        return []
      }
    },
  },
  getters: {
    getArticlesByCategory: (state) => (category) => {
      return state.articles.filter((article) => article.category === category)
    },
    getArticlesBySubcategory: (state) => (category, subcategory) => {
      return state.articles.filter(
        (article) => article.category === category && article.subcategory === subcategory
      )
    },
    getArticlesByTag: (state) => (tag) => {
      return state.articles.filter(
        (article) => article.tags && article.tags.includes(tag)
      )
    },
    getArticlesByGameHub: (state) => (gameHubSlug) => {
      return state.articles.filter((article) => article.gameHub === gameHubSlug)
    },
    getFeaturedArticles: (state) => (limit = 4) => {
      return state.articles
        .sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate))
        .slice(0, limit)
    },
    getLatestArticlesByCategory: (state) => (category, limit = 4) => {
      return state.articles
        .filter((article) => article.category === category)
        .sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate))
        .slice(0, limit)
    },
    getRelatedByTags: (state) => (tags, excludeSlug, limit = 3) => {
      return state.articles
        .filter((article) => article.slug !== excludeSlug && article.tags && tags.some((t) => article.tags.includes(t)))
        .map((article) => ({
          ...article,
          _matchCount: article.tags.filter((t) => tags.includes(t)).length,
        }))
        .slice()
        .sort((a, b) => b._matchCount - a._matchCount || new Date(b.publishDate) - new Date(a.publishDate))
        .slice(0, limit)
        .map(({ _matchCount, ...article }) => article)
    },
    getRelatedByCategory: (state) => (category, excludeSlug, limit = 3) => {
      return state.articles
        .filter((article) => article.category === category && article.slug !== excludeSlug)
        .slice()
        .sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate))
        .slice(0, limit)
    },
    getLatestArticles: (state) => (limit = 3) => {
      return state.articles
        .slice()
        .sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate))
        .slice(0, limit)
    },
    getPrevNextArticle: (state) => (category, slug) => {
      const sorted = state.articles
        .filter((article) => article.category === category)
        .slice()
        .sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate))
      const index = sorted.findIndex((article) => article.slug === slug)
      if (index === -1) return { prev: null, next: null }
      return {
        prev: index > 0 ? sorted[index - 1] : null,
        next: index < sorted.length - 1 ? sorted[index + 1] : null,
      }
    },
    getAllTags: (state) => () => {
      const tagMap = {}
      state.articles.forEach((article) => {
        if (article.tags) {
          article.tags.forEach((tag) => {
            tagMap[tag] = (tagMap[tag] || 0) + 1
          })
        }
      })
      return Object.entries(tagMap)
        .map(([tag, count]) => ({ tag, count }))
        .sort((a, b) => b.count - a.count)
    },
  },
}
